import {
  getRelatedPosts,
  getRelatedPostsMetadata,
} from '@wix/ambassador-blog-frontend-adapter-public-v2-related-post/http';
import {
  GetRelatedPostsMetadataResponse,
  GetRelatedPostsResponse,
  Metrics,
  RelatedPost,
  RelatedPostMetadata,
} from '@wix/ambassador-blog-frontend-adapter-public-v2-related-post/types';
import setPosts from '../../common/actions/set-posts';
import {
  getLanguageCode,
  getQueryLocale,
} from '../../common/selectors/locale-selectors';
import {
  enhancePostsWithMetadata,
  normalizePosts,
} from '../../common/services/post-utils';
import { setIsLoading } from '../../common/store/is-loading/is-loading-actions';
import { fetchTranslationsSuccess } from '../../common/store/translations/translations-actions';
import { AppState } from '../../common/types';
import { getRelatedPostCount } from '../selectors/related-posts-selectors';
import { RelatedPostsThunkAction } from '../types';

export const RELATED_POSTS_ENTITY = 'related-posts';

export function fetchRelatedPostsRenderModel(
  postId: string | undefined,
): RelatedPostsThunkAction {
  return async (dispatch, getState, { httpClient }) => {
    const state = getState();

    dispatch(setIsLoading(RELATED_POSTS_ENTITY, undefined, true));

    if (!postId) {
      dispatch(setPosts([]));
      dispatch(setIsLoading(RELATED_POSTS_ENTITY, undefined, false));
      return;
    }

    const locale = getQueryLocale(state);
    const languageCode = getLanguageCode(state);
    const pageSize = getRelatedPostCount(state);

    const [rawPosts, metadata] = await Promise.all([
      httpClient.request(
        getRelatedPosts({
          postId,
          languageCode,
          locale,
          pageSize,
          translationsName: 'related-posts-widget',
          includeInitialPageData: true,
        }),
      ),
      httpClient.request(
        getRelatedPostsMetadata({ postId, languageCode, pageSize }),
      ),
    ]);

    const posts = transformResponse(state, rawPosts.data, metadata.data);

    dispatch(fetchTranslationsSuccess(rawPosts.data.translations));
    dispatch(setPosts(posts));
    dispatch(setIsLoading(RELATED_POSTS_ENTITY, undefined, false));
  };
}

function transformResponse(
  state: AppState,
  postsResponse: GetRelatedPostsResponse,
  metadataResponse: GetRelatedPostsMetadataResponse,
) {
  const posts = enhancePostsWithMetadata(
    postsResponse.relatedPosts ?? [],
    mapMetadataByPostId(metadataResponse.relatedPostsMetadata ?? []),
  );

  const postsWithCoverMedia = applyCoverMedia(posts);

  const normalizedPosts = normalizePosts({
    state,
    posts: postsWithCoverMedia as any,
    blogCategoryIds: [],
    origin: '/v3/posts',
  });

  return normalizedPosts;
}

function applyCoverMedia(posts: RelatedPost[]) {
  return posts.map((_post) => ({
    ..._post,
    coverMedia: { ..._post.media, ..._post.media?.wixMedia },
  }));
}

function mapMetadataByPostId(metadata: RelatedPostMetadata[]) {
  return metadata.reduce<Record<string, Metrics>>((result, { id, metrics }) => {
    if (id && metrics) {
      result[id] = metrics;
    }
    return result;
  }, {});
}
